import { useEffect, useRef, useState, useCallback, type CSSProperties } from "react";

/**
 * DeploymentFlow — wireframe SVG showing user apps streaming into a datacenter
 * with multiple racks. Cables drop into a floor tray, run right, then rise
 * into their assigned rack — like real DC cabling.
 *
 * Theming: drives everything off shadcn semantic tokens
 * (--foreground, --muted-foreground, --primary, --border, --card). It inherits
 * light/dark automatically — no accent prop, no hardcoded colors.
 *
 * Scroll-driven via the `progress` prop (0 → 1). Respects prefers-reduced-motion.
 */

type HardwareType = "switch" | "server" | "storage" | "blades" | "patch";

const APPS = [
  { name: "nextjs", meta: "main · 2s ago" },
  { name: "fastapi", meta: "main · 1h ago" },
  { name: "postgres", meta: "service · online" },
  { name: "n8n", meta: "main · 1m ago" },
  { name: "landing", meta: "main · 1d ago" },
  { name: "redis", meta: "service · online" }
];

const N_PATHS = APPS.length;
const MAX_PACKETS = 4;

const RACKS = [{ x: 380 }, { x: 520 }, { x: 660 }];
const RACK_Y = 120;
const RACK_W = 120;
const RACK_H = 290;
const UNITS_PER_RACK = 5;

// Mixed hardware per rack so no two racks read the same. Each rack has
// UNITS_PER_RACK entries drawn top→bottom.
const RACK_LAYOUT: HardwareType[][] = [
  ["switch", "server", "server", "storage", "blades"],
  ["patch", "switch", "server", "storage", "server"],
  ["server", "blades", "switch", "storage", "server"]
];

const APP_Y = [76, 142, 208, 274, 340, 406];

const RACK_TOP_Y = RACK_Y; // cables enter the top edge here
const RACK_BOT_Y = RACK_Y + RACK_H + 2; // ...and the bottom edge here

// Symmetric feed: the top cable and bottom cable for a given rack enter at the
// SAME x (the rack's centre line), and both sets run in the same rack order.
const ROUTES = [
  { rack: 0, riser: 440, lane: 72, side: "top" as const },
  { rack: 1, riser: 580, lane: 80, side: "top" as const },
  { rack: 2, riser: 720, lane: 88, side: "top" as const },
  { rack: 0, riser: 440, lane: 440, side: "bottom" as const },
  { rack: 1, riser: 580, lane: 448, side: "bottom" as const },
  { rack: 2, riser: 720, lane: 456, side: "bottom" as const }
];

// x at which the cables have finished fanning out and settle into the tray
const TRAY_START = 300;

const PATHS = ROUTES.map(({ riser, lane, side }, i) => {
  const y = APP_Y[i];
  if (side === "top") {
    return [
      `M 172 ${y}`,
      `C 230 ${y}, 250 ${lane}, ${TRAY_START} ${lane}`, // curve up into overhead tray
      `L ${riser - 40} ${lane}`, // run right along the tray
      `Q ${riser} ${lane}, ${riser} ${lane + 18}`, // bend down
      `L ${riser} ${RACK_TOP_Y}` // drop into rack top
    ].join(" ");
  }
  return [
    `M 172 ${y}`,
    `C 230 ${y}, 250 ${lane}, ${TRAY_START} ${lane}`, // curve down into floor tray
    `L ${riser - 40} ${lane}`, // run right along the floor
    `Q ${riser} ${lane}, ${riser} ${lane - 18}`, // bend up
    `L ${riser} ${RACK_BOT_Y}` // rise into rack bottom
  ].join(" ");
});

// semantic tokens — resolve against the host app's shadcn theme
const C = {
  line: "var(--foreground)", // crisp ink in light mode, near-white in dark — matches the landing
  text: "var(--foreground)",
  dim: "var(--muted-foreground)",
  accent: "var(--primary)",
  fill: "var(--card)"
};

const DeploymentFlowGraphics = ({
  progress = 0,
  className = ""
}: {
  progress?: number;
  className?: string;
}) => {
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const packetRefs = useRef<(SVGCircleElement | null)[][]>([]);
  const lengthsRef = useRef<number[]>([]);
  const progressRef = useRef(progress);
  const rafRef = useRef(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    lengthsRef.current = pathRefs.current.map(p => (p ? p.getTotalLength() : 0));
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      return;
    }
    const tick = (now: number) => {
      const p = progressRef.current;
      const active = Math.max(1, Math.ceil(p * N_PATHS));
      const packetsPerPath = 1 + Math.floor(p * (MAX_PACKETS - 1));
      const speed = 0.06 + p * 0.22;

      for (let i = 0; i < N_PATHS; i++) {
        const path = pathRefs.current[i];
        const len = lengthsRef.current[i];
        if (!path || !len) {
          continue;
        }
        for (let j = 0; j < MAX_PACKETS; j++) {
          const dot = packetRefs.current[i]?.[j];
          if (!dot) {
            continue;
          }
          const visible = i < active && j < packetsPerPath;
          if (!visible) {
            dot.setAttribute("opacity", "0");
            continue;
          }
          const t = ((now / 1000) * speed + j / packetsPerPath + i * 0.17) % 1;
          const pt = path.getPointAtLength(t * len);
          dot.setAttribute("cx", String(pt.x));
          dot.setAttribute("cy", String(pt.y));
          const fade = Math.min(1, t * 8, (1 - t) * 8);
          dot.setAttribute("opacity", String(0.95 * fade));
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reducedMotion]);

  const active = Math.max(1, Math.ceil(progress * N_PATHS));
  const glow = 0.35 + progress * 0.65; // stroke opacity for active elements
  const ledDuration = Math.max(0.35, 1.6 - progress * 1.2);
  const totalUnits = RACKS.length * UNITS_PER_RACK;
  const rackActive = [active >= 1, active >= 2, active >= 3];

  const setPacketRef = useCallback(
    (i: number, j: number) => (el: SVGCircleElement | null) => {
      if (!packetRefs.current[i]) {
        packetRefs.current[i] = [];
      }
      packetRefs.current[i][j] = el;
    },
    []
  );

  // draws one rack unit's front-panel detail by hardware type
  const unit = (
    type: HardwareType,
    u: number,
    r: number,
    rx: number,
    uy: number,
    unitOn: boolean
  ) => {
    const x0 = rx + 10; // inside the mounting rails
    const w = RACK_W - 20; // usable face width
    const cx = x0 + 18; // content starts after the left bezel
    const ledAnim: CSSProperties = unitOn
      ? {
          animation: `dd-led ${ledDuration + u * 0.13 + r * 0.07}s ease-in-out infinite`,
          filter: "url(#dd-glow)"
        }
      : { opacity: 0.3 };
    const acc = unitOn ? C.accent : "currentColor";
    const S = (o: number) => ({ stroke: "currentColor", strokeOpacity: o });

    // shared bezel: chassis outline + a live status LED pair on the left ear
    const bezel = (
      <>
        <rect x={x0} y={uy} width={w} height="38" rx="3" fill="none" {...S(0.5)} />
        <line x1={x0 + 14} y1={uy + 4} x2={x0 + 14} y2={uy + 34} {...S(0.25)} />
        <circle
          cx={x0 + 7}
          cy={uy + 11}
          r="2.3"
          fill={acc}
          className={unitOn ? "dd-anim" : ""}
          style={ledAnim}
        />
        <circle
          cx={x0 + 7}
          cy={uy + 20}
          r="2.3"
          fill="currentColor"
          opacity={unitOn ? 0.55 : 0.3}
        />
        <circle
          cx={x0 + 7}
          cy={uy + 29}
          r="2.3"
          fill="currentColor"
          opacity={unitOn ? 0.35 : 0.2}
        />
      </>
    );

    if (type === "switch") {
      // 1U switch: a long row of ethernet ports + 2 SFP cages, link LEDs above
      const ports = 10;
      const pw = (w - 20) / ports;
      return (
        <g>
          {bezel}
          {Array.from({ length: ports }).map((_, i) => (
            <g key={i}>
              <rect
                x={cx + i * pw}
                y={uy + 20}
                width={pw - 1.5}
                height="9"
                rx="1"
                fill="none"
                {...S(0.4)}
              />
              <circle
                cx={cx + i * pw + (pw - 1.5) / 2}
                cy={uy + 14}
                r="1.3"
                fill={unitOn && i % 2 === 0 ? acc : "currentColor"}
                className={unitOn && i % 2 === 0 ? "dd-anim" : ""}
                style={unitOn && i % 2 === 0 ? ledAnim : { opacity: 0.35 }}
              />
            </g>
          ))}
        </g>
      );
    }

    if (type === "server") {
      // 2U server: vented intake grille + drive bays w/ handle notches
      const vents = 6;
      const bays = 4;
      const bw = 12;
      return (
        <g>
          {bezel}
          {Array.from({ length: vents }).map((_, i) => (
            <line
              key={i}
              x1={cx + i * 2.6}
              y1={uy + 7}
              x2={cx + i * 2.6}
              y2={uy + 31}
              {...S(0.22)}
            />
          ))}
          {Array.from({ length: bays }).map((_, i) => {
            const bx = x0 + w - 8 - (bays - i) * (bw + 2);
            return (
              <g key={i}>
                <rect
                  x={bx}
                  y={uy + 6}
                  width={bw}
                  height="26"
                  rx="1.5"
                  fill="none"
                  {...S(0.4)}
                />
                <line x1={bx + 3} y1={uy + 9} x2={bx + 3} y2={uy + 29} {...S(0.3)} />
                <circle
                  cx={bx + bw - 3}
                  cy={uy + 10}
                  r="1.2"
                  fill={unitOn && i < 3 ? acc : "currentColor"}
                  className={unitOn && i < 3 ? "dd-anim" : ""}
                  style={unitOn && i < 3 ? ledAnim : { opacity: 0.3 }}
                />
              </g>
            );
          })}
        </g>
      );
    }

    if (type === "blades") {
      // blade chassis: vertical blade slots, each with its own LED + handle
      const blades = 6;
      const bw = (w - 22) / blades;
      return (
        <g>
          {bezel}
          {Array.from({ length: blades }).map((_, i) => {
            const bx = cx + i * bw;
            const liveB = unitOn && i < Math.round(progress * blades);
            return (
              <g key={i}>
                <rect
                  x={bx}
                  y={uy + 6}
                  width={bw - 1.5}
                  height="26"
                  rx="1"
                  fill="none"
                  {...S(0.4)}
                />
                <line x1={bx + 2} y1={uy + 28} x2={bx + bw - 3.5} y2={uy + 28} {...S(0.3)} />
                <circle
                  cx={bx + (bw - 1.5) / 2}
                  cy={uy + 10}
                  r="1.3"
                  fill={liveB ? acc : "currentColor"}
                  className={liveB ? "dd-anim" : ""}
                  style={liveB ? ledAnim : { opacity: 0.3 }}
                />
              </g>
            );
          })}
        </g>
      );
    }

    if (type === "storage") {
      // storage array: grid of drive carriers, activity LEDs sprinkled across
      const cols = 8;
      const rows = 2;
      const gw = (w - 24) / cols;
      const gh = 13;
      return (
        <g>
          {bezel}
          {Array.from({ length: rows }).map((_, ry) =>
            Array.from({ length: cols }).map((_, cxi) => {
              const gx = cx + cxi * gw;
              const gy = uy + 5 + ry * (gh + 2);
              const idx = ry * cols + cxi;
              const liveG = unitOn && idx < Math.round(progress * cols * rows);
              return (
                <g key={`${ry}-${cxi}`}>
                  <rect
                    x={gx}
                    y={gy}
                    width={gw - 2}
                    height={gh}
                    rx="1"
                    fill="none"
                    {...S(0.38)}
                  />
                  <line
                    x1={gx + 2}
                    y1={gy + gh / 2}
                    x2={gx + gw - 6}
                    y2={gy + gh / 2}
                    {...S(0.22)}
                  />
                  <circle
                    cx={gx + gw - 4.5}
                    cy={gy + 3.5}
                    r="1.1"
                    fill={liveG ? acc : "currentColor"}
                    className={liveG ? "dd-anim" : ""}
                    style={liveG ? ledAnim : { opacity: 0.3 }}
                  />
                </g>
              );
            })
          )}
        </g>
      );
    }

    // patch panel: dense two-row port field + a cable-management bar
    const cols = 12;
    const pw = (w - 24) / cols;
    return (
      <g>
        {bezel}
        {[0, 1].map(row =>
          Array.from({ length: cols }).map((_, i) => (
            <rect
              key={`${row}-${i}`}
              x={cx + i * pw}
              y={uy + 6 + row * 11}
              width={pw - 1.2}
              height="8"
              rx="0.8"
              fill="none"
              {...S(0.35)}
            />
          ))
        )}
        <line
          x1={x0 + 4}
          y1={uy + 31}
          x2={x0 + w - 4}
          y2={uy + 31}
          {...S(0.3)}
          strokeDasharray="3 3"
        />
      </g>
    );
  };

  return (
    <div className={`relative mx-auto flex w-full items-center justify-center ${className}`}>
      <style>{`
        @keyframes dd-dash { to { stroke-dashoffset: -32; } }
        @keyframes dd-led { 0%, 60% { opacity: .25; } 70%, 100% { opacity: 1; } }
        @keyframes dd-scan { 0% { transform: translateY(0); } 100% { transform: translateY(224px); } }
        @media (prefers-reduced-motion: reduce) { .dd-anim { animation: none !important; } }
      `}</style>

      <svg
        viewBox="0 0 850 500"
        className="relative mx-auto block h-auto w-full max-w-3xl"
        style={{ color: C.line }}
        role="img"
        aria-label="Diagram: applications deploying into a datacenter with multiple racks, connections increasing with scroll"
      >
        <defs>
          <filter id="dd-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="dd-rack-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.accent} stopOpacity="0.08" />
            <stop offset="100%" stopColor={C.accent} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* connection paths */}
        <g fill="none">
          {PATHS.map((d, i) => {
            const isActive = i < active;
            return (
              <g key={i}>
                <path d={d} stroke="currentColor" strokeOpacity="0.28" strokeWidth="1" />
                <path
                  ref={el => {
                    pathRefs.current[i] = el;
                  }}
                  d={d}
                  stroke={C.accent}
                  strokeWidth="1.5"
                  strokeDasharray="6 10"
                  className="dd-anim"
                  style={{
                    opacity: isActive ? glow : 0,
                    transition: "opacity .6s ease",
                    animation: `dd-dash ${1.6 - progress * 1.1}s linear infinite`,
                    filter: "url(#dd-glow)"
                  }}
                />
                {Array.from({ length: MAX_PACKETS }).map((_, j) => (
                  <circle
                    key={j}
                    ref={setPacketRef(i, j)}
                    r="3"
                    fill={C.accent}
                    opacity="0"
                    style={{ filter: "url(#dd-glow)" }}
                  />
                ))}
              </g>
            );
          })}
        </g>

        {/* app nodes */}
        {APPS.map((app, i) => {
          const y = APP_Y[i] - 22;
          const isActive = i < active;
          return (
            <g
              key={app.name}
              style={{ opacity: isActive ? 1 : 0.4, transition: "opacity .6s ease" }}
            >
              <rect
                x="36"
                y={y}
                width="136"
                height="46"
                rx="8"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.55"
                strokeWidth="1"
              />
              <rect
                x="36"
                y={y}
                width="136"
                height="46"
                rx="8"
                fill="none"
                stroke={C.accent}
                strokeWidth="1"
                style={{
                  opacity: isActive ? 0.5 * glow : 0
                }}
              />
              <circle
                cx="52"
                cy={y + 16}
                r="3.5"
                fill={isActive ? C.accent : "currentColor"}
                className={isActive ? "dd-anim" : ""}
                style={
                  isActive
                    ? { animation: `dd-led ${ledDuration}s ease-in-out infinite` }
                    : { opacity: 0.4 }
                }
              />
              <text
                x="64"
                y={y + 20}
                fill={C.text}
                fontSize="12"
                fontFamily="ui-monospace, monospace"
              >
                {app.name}
              </text>
              <text
                x="52"
                y={y + 37}
                fill={C.dim}
                fontSize="9"
                fontFamily="ui-monospace, monospace"
              >
                {app.meta}
              </text>
            </g>
          );
        })}

        {/* racks */}
        {RACKS.map((rack, r) => {
          const rx = rack.x;
          const on = rackActive[r];
          return (
            <g key={rx} style={{ opacity: on ? 1 : 0.45, transition: "opacity .6s ease" }}>
              <polygon
                points={`${rx},${RACK_Y} ${rx + 30},${RACK_Y - 22} ${rx + 150},${RACK_Y - 22} ${rx + 120},${RACK_Y}`}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.5"
                strokeWidth="1"
              />
              <polygon
                points={`${rx + 120},${RACK_Y} ${rx + 150},${RACK_Y - 22} ${rx + 150},${RACK_Y + RACK_H - 22} ${rx + 120},${RACK_Y + RACK_H}`}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.32"
                strokeWidth="1"
              />
              <rect
                x={rx}
                y={RACK_Y}
                width={RACK_W}
                height={RACK_H}
                rx="0"
                fill="url(#dd-rack-fill)"
                stroke="currentColor"
                strokeOpacity="0.65"
                strokeWidth="1.2"
              />
              <rect
                x={rx}
                y={RACK_Y}
                width={RACK_W}
                height={RACK_H}
                rx="4"
                fill="none"
                stroke={C.accent}
                strokeWidth="1"
                style={{
                  opacity: on ? 0.4 * glow : 0,
                  filter: "url(#dd-glow)",
                  transition: "opacity .6s ease"
                }}
              />

              {on && (
                <rect
                  x={rx + 2}
                  y={RACK_Y + 4}
                  width={RACK_W - 4}
                  height="16"
                  fill={C.accent}
                  className="dd-anim"
                  style={{
                    opacity: 0.06 + progress * 0.09,
                    animation: "dd-scan 3.5s linear infinite alternate",
                    animationDelay: `${r * 0.9}s`
                  }}
                />
              )}

              {RACK_LAYOUT[r].map((type, u) => {
                const uy = RACK_Y + 12 + u * 46;
                const unitOn = progress * totalUnits > r * UNITS_PER_RACK + u;
                return (
                  <g
                    key={u}
                    style={{ opacity: unitOn ? 1 : 0.4, transition: "opacity .5s ease" }}
                  >
                    {unit(type, u, r, rx, uy, unitOn)}
                  </g>
                );
              })}

              <text
                x={rx + 8}
                y={RACK_Y + RACK_H - 6}
                fill={C.dim}
                fontSize="8"
                fontFamily="ui-monospace, monospace"
                letterSpacing="1.5"
              >
                RACK 0{r + 1}
              </text>
            </g>
          );
        })}

        {ROUTES.map(({ riser, side }, i) => {
          const cy = side === "top" ? RACK_TOP_Y : RACK_BOT_Y - 1;
          return (
            <circle
              key={`${riser}-${side}`}
              cx={riser}
              cy={cy}
              r="3.5"
              fill={i < active ? C.accent : "none"}
              stroke="currentColor"
              strokeOpacity="0.6"
              style={{
                opacity: i < active ? glow : 0.5,
                transition: "opacity .6s ease",
                filter: i < active ? "url(#dd-glow)" : "none"
              }}
            />
          );
        })}
      </svg>
    </div>
  );
};

/** Scroll-driven marketing section: 300vh tall, sticky visual, progress = scroll depth. */
export const DeploymentFlow = () => {
  const [progress, setProgress] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      // Drive progress off the section's position in the viewport: it starts
      // (0) when the section's top sits at the bottom of the viewport, and
      // fills to full (1) by the time that top rises to ~40% up the viewport,
      // so the graphic is fully lit around the centre rather than off-screen.
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const fullAt = vh * 0.4; // rect.top at which progress reaches 1
      const p = vh > 0 ? Math.min(1, Math.max(0, (vh - rect.top) / (vh - fullAt))) : 0;
      setProgress(p);
    };
    const handleScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <section className="relative py-24">
      <div className="sticky top-0 flex flex-col items-center justify-center px-6">
        <div className="mx-auto text-center mb-10">
          <div className="inline-block text-xs text-primary bg-primary/8 px-3 py-1 rounded-full mb-4 font-medium tracking-wide lowercase font-mono">
            your app goes live in seconds
          </div>
          <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
            From <em className="text-primary">git push</em> to your own datacenter.
          </h2>
          <p className="text-[17px] leading-relaxed text-muted-foreground text-pretty">
            Your every code change routes through the deploy engine and lands on the servers
            you own.
          </p>
        </div>
        <div className="mx-auto w-full max-w-3xl text-foreground">
          <DeploymentFlowGraphics progress={progress} />
        </div>
        {/* use this div to measure the height of the section */}
        <div ref={ref} />
      </div>
    </section>
  );
};
